// The Cases screen part: the nav item, /me/cases (the list beside the open
// Case), the events that change what it shows, the favorite kind, and the keys
// `c` (a new Case) and `g a` (Cases), and a Case shown in a side view. Hosts
// add `casesScreen({ live, stepMenu })` (the context is edited live, through
// the host's transport; `stepMenu` is what other parts add to a step's "…").

import type { QueryClient } from "@tanstack/react-query";
import { createRoute, Outlet, useParams } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { lazyScreen, withLazy } from "../app/lazy";
import type { MeRouterContext } from "../app/routes";
import { defineScreenPart, type SideViewProps } from "../app/screen-part";
import type { LiveTransport } from "../documents/live";
import { invalidateMemo } from "../memos/queries";
import { refPrefix } from "../refs/queries";
import { ListDetail } from "../shell/list-detail";
import { useSideView } from "../shell/side-view";
import { iconOf } from "../ui/icon";
import { type CasesExtensions, CasesExtensionsProvider } from "./extensions";
import {
  caseChanged,
  caseContextLiveQuery,
  caseDeleted,
  caseQuery,
  casesPrefix,
  invalidateCases,
} from "./queries";
import { CasesScreenProvider, useCasesScreen } from "./screen-state";

// The screens are loaded when first opened (app/lazy.tsx).
const CasesList = lazyScreen(() => import("./list"), "CasesList");
const CaseRoute = lazyScreen(() => import("./detail"), "CaseRoute");
const CasePanel = lazyScreen(() => import("./panel"), "CasePanel");

const caseIdOf = (payload: unknown) => {
  const id = (payload as { caseId?: unknown } | null)?.caseId;
  return typeof id === "string" ? id : undefined;
};

const noop = () => {};

const dropRef = (qc: QueryClient, id: string | undefined) => {
  if (id) void qc.invalidateQueries({ queryKey: refPrefix("case", id) });
};

function CasesScreen() {
  return (
    <CasesScreenProvider>
      <CasesFrame />
    </CasesScreenProvider>
  );
}

function CasesFrame() {
  const { caseId } = useParams({ strict: false }) as { caseId?: string };
  const { opened } = useCasesScreen();
  const openCaseId = caseId && caseId !== "new" ? caseId : null;
  return (
    <ListDetail
      list={
        <CasesList
          openCaseId={openCaseId}
          openStepId={opened?.caseId === openCaseId ? opened.stepId : null}
        />
      }
      detail={<Outlet />}
      showing={caseId ? "detail" : "list"}
      listPath="/me/cases"
      listLabel={m.cases_list()}
    />
  );
}

export interface CasesScreenOptions extends CasesExtensions {
  /** How this host reaches live editing (the context is edited live). */
  live: LiveTransport;
}

export const casesScreen = (options: CasesScreenOptions) =>
  defineScreenPart({
    id: "cases",
    nav: {
      order: 20,
      label: () => m.cases_title(),
      icon: iconOf("cases"),
      to: "/me/cases",
      remembers: true,
    },
    routes: (main) => {
      const cases = createRoute({
        getParentRoute: () => main,
        path: "cases",
        component: withLazy(CasesScreen, CasesList),
      });
      // Nothing open: the list is alone and no detail is drawn.
      const none = createRoute({ getParentRoute: () => cases, path: "/" });
      const one = createRoute({
        getParentRoute: () => cases,
        path: "$caseId",
        // A missing Case is told in the detail, not by stopping the route.
        loader: async ({ context, params }) => {
          if (params.caseId === "new") return;
          const c = context as MeRouterContext;
          await Promise.all([
            c.queryClient
              .ensureQueryData(caseQuery(c.api, params.caseId))
              .catch(() => undefined),
            c.queryClient
              .ensureQueryData(caseContextLiveQuery(c.api, params.caseId))
              .catch(() => undefined),
          ]);
        },
        component: withLazy(
          () => (
            <CasesExtensionsProvider extensions={options}>
              <CaseRoute transport={options.live} />
            </CasesExtensionsProvider>
          ),
          CaseRoute,
        ),
      });
      return [cases.addChildren([none, one])];
    },
    realtime: {
      "case.changed": (payload, { qc }) => {
        const id = caseIdOf(payload);
        caseChanged(qc, id);
        dropRef(qc, id);
      },
      "case.deleted": (payload, { qc }) => {
        const id = caseIdOf(payload);
        caseDeleted(qc, id);
        dropRef(qc, id);
      },
      "label.changed": (_payload, { qc }) => void invalidateCases(qc),
      "label.removed": (_payload, { qc }) => void invalidateCases(qc),
      "person.changed": (_payload, { qc }) => void invalidateCases(qc),
      "agent_memo.changed": (payload, { qc }) => {
        const { kind, id } = (payload ?? {}) as {
          kind?: unknown;
          id?: unknown;
        };
        if (kind !== "case" || typeof id !== "string") return;
        void invalidateMemo(qc, "case", id);
        caseChanged(qc, id);
      },
    },
    favoriteKinds: {
      case: { icon: iconOf("cases"), to: (id) => `/me/cases/${id}` },
      // An Action, as a waiting's target: it opens its Case (TargetName also
      // opens the step there, as the old web's), and is named with its Case.
      action: {
        icon: iconOf("action"),
        to: (_id, path) =>
          typeof path?.caseId === "string"
            ? `/me/cases/${path.caseId}`
            : "/me/cases",
        waitName: ({ label, path }) =>
          [path?.caseTitle, label]
            .filter((s) => typeof s === "string" && s !== "")
            .join(" › ") || label,
      },
    },
    // The same detail as the panel cloud opens beside a message: writable, and
    // deleting the Case closes the side view instead of going to the list.
    sideViews: {
      case: function CaseSideView({ id }: SideViewProps) {
        const side = useSideView();
        return (
          <CasesExtensionsProvider extensions={options}>
            <CasePanel
              caseId={id}
              transport={options.live}
              onClose={side?.close ?? noop}
            />
          </CasesExtensionsProvider>
        );
      },
    },
    shortcuts: [
      {
        id: "cases.new",
        key: "c",
        group: "create",
        label: () => m.cases_new(),
        run: ({ navigate }) => navigate("/me/cases/new"),
      },
      {
        id: "cases.go",
        key: "g a",
        group: "navigate",
        label: () => m.cases_go(),
        run: ({ navigate }) => navigate("/me/cases"),
      },
    ],
    persist: (key) =>
      key[0] === casesPrefix[0] &&
      key[1] === casesPrefix[1] &&
      (key[2] === "detail" || (key[2] === "list" && key[3] === "open")),
  });
